/**
 * playerStep.ts
 *
 * The first-person controller's collision-aware move, kept free of React and
 * three.js so it can be tested directly.
 *
 * Why this exists: the move used to test walkability once, at the end point
 * of `velocity * dt`. A long frame (a hitch, a loaded machine, a return to a
 * hidden tab while holding a key) made that end point metres away, past any
 * wall or shut gate in between, and the player tunnelled through. The frame
 * delta is now capped (`MAX_CONTROLLER_DT`) and the move is split into
 * substeps no longer than `MAX_STEP_DISTANCE`, each tested on its own.
 */

/**
 * The longest frame the controller simulates in one go, in seconds. A slower
 * frame plays as slow motion rather than a jump.
 */
export const MAX_CONTROLLER_DT = 1 / 20;

/**
 * The furthest the player may travel between two walkability tests, in
 * metres. Well under the thinnest blocker plus a player's diameter.
 */
export const MAX_STEP_DISTANCE = 0.1;

/** The map runtime's walkability predicate (`MapRuntime.isWalkable`). */
export type WalkablePredicate = (
  x: number,
  z: number,
  radius: number,
  floor: number,
  y: number,
) => boolean;

/** The controller's delta for this frame: the raw delta capped to `MAX_CONTROLLER_DT`. */
export function controllerDelta(rawDt: number): number {
  if (!Number.isFinite(rawDt) || rawDt <= 0) return 0;
  return Math.min(rawDt, MAX_CONTROLLER_DT);
}

/**
 * Moves `pos` by `vel * dt` on the ground plane, sliding along whatever
 * blocks it. Each substep tries the full move, then each axis on its own, and
 * zeroes the velocity component that stays blocked. Mutates `pos` and `vel`.
 */
export function stepWithCollision(
  pos: { x: number; z: number },
  vel: { x: number; z: number },
  dt: number,
  radius: number,
  floor: number,
  y: number,
  isWalkable: WalkablePredicate,
): void {
  const distance = Math.hypot(vel.x, vel.z) * dt;
  const steps = Math.max(1, Math.ceil(distance / MAX_STEP_DISTANCE));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    const nx = pos.x + vel.x * h;
    const nz = pos.z + vel.z * h;
    if (isWalkable(nx, nz, radius, floor, y)) {
      pos.x = nx;
      pos.z = nz;
      continue;
    }
    if (isWalkable(nx, pos.z, radius, floor, y)) pos.x = nx;
    else vel.x = 0;
    if (isWalkable(pos.x, nz, radius, floor, y)) pos.z = nz;
    else vel.z = 0;
  }
}
